import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Shield, Settings, BarChart3, Target } from 'lucide-react';

const Cookies = ({ language = 'de' }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-4" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'de' ? 'Zurück zur Startseite' : 'Back to Home'}
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="cookies-title">
            {language === 'de' ? 'Cookie-Richtlinien' : 'Cookie Policy'}
          </h1>
          <p className="text-gray-600">INTERLINGUA.APP by MYSYMP AG</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8 prose max-w-none" data-testid="cookies-content">
          
          <div className="flex items-center gap-3 mb-6 p-4 bg-emerald-50 rounded-lg">
            <Cookie className="w-8 h-8 text-emerald-600 flex-shrink-0" />
            <p className="text-gray-700 m-0">
              {language === 'de' 
                ? 'Diese Seite erklärt, wie INTERLINGUA.APP Cookies und ähnliche Technologien verwendet, um Ihnen das beste Erlebnis zu bieten.'
                : 'This page explains how INTERLINGUA.APP uses cookies and similar technologies to provide you with the best experience.'}
            </p>
          </div>

          <h2 className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-600" />
            {language === 'de' ? '1. Was sind Cookies?' : '1. What are Cookies?'}
          </h2>
          <p>
            {language === 'de' 
              ? 'Cookies sind kleine Textdateien, die auf Ihrem Gerät gespeichert werden, wenn Sie unsere Website besuchen. Sie helfen uns, Ihre Präferenzen zu speichern und das Nutzererlebnis zu verbessern.'
              : 'Cookies are small text files stored on your device when you visit our website. They help us remember your preferences and improve your user experience.'}
          </p>

          <h2 className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-emerald-600" />
            {language === 'de' ? '2. Welche Cookies verwenden wir?' : '2. What Cookies Do We Use?'}
          </h2>

          <h3>{language === 'de' ? '2.1 Notwendige Cookies' : '2.1 Essential Cookies'}</h3>
          <p>
            {language === 'de' 
              ? 'Diese Cookies sind für das Funktionieren der Website unbedingt erforderlich:'
              : 'These cookies are strictly necessary for the website to function:'}
          </p>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2 text-left">Cookie</th>
                <th className="border p-2 text-left">{language === 'de' ? 'Zweck' : 'Purpose'}</th>
                <th className="border p-2 text-left">{language === 'de' ? 'Dauer' : 'Duration'}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border p-2">auth_token</td>
                <td className="border p-2">{language === 'de' ? 'Authentifizierung & Login' : 'Authentication & Login'}</td>
                <td className="border p-2">{language === 'de' ? '7 Tage' : '7 days'}</td>
              </tr>
              <tr>
                <td className="border p-2">user_preferences</td>
                <td className="border p-2">{language === 'de' ? 'Spracheinstellungen, Theme' : 'Language settings, Theme'}</td>
                <td className="border p-2">{language === 'de' ? '1 Jahr' : '1 year'}</td>
              </tr>
              <tr>
                <td className="border p-2">session_id</td>
                <td className="border p-2">{language === 'de' ? 'Sitzungsverwaltung' : 'Session management'}</td>
                <td className="border p-2">{language === 'de' ? 'Sitzung' : 'Session'}</td>
              </tr>
            </tbody>
          </table>

          <h3 className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-emerald-600" />
            {language === 'de' ? '2.2 Analyse-Cookies' : '2.2 Analytics Cookies'}
          </h3>
          <p>
            {language === 'de' 
              ? 'Diese Cookies helfen uns zu verstehen, wie Besucher unsere Website nutzen:'
              : 'These cookies help us understand how visitors use our website:'}
          </p>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2 text-left">Cookie</th>
                <th className="border p-2 text-left">{language === 'de' ? 'Anbieter' : 'Provider'}</th>
                <th className="border p-2 text-left">{language === 'de' ? 'Zweck' : 'Purpose'}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border p-2">_ga</td>
                <td className="border p-2">Google Analytics</td>
                <td className="border p-2">{language === 'de' ? 'Besucher-Statistiken' : 'Visitor statistics'}</td>
              </tr>
            </tbody>
          </table>

          <h3 className="flex items-center gap-2">
            <Target className="w-4 h-4 text-emerald-600" />
            {language === 'de' ? '2.3 Funktionale Cookies' : '2.3 Functional Cookies'}
          </h3>
          <p>
            {language === 'de' 
              ? 'Diese Cookies ermöglichen erweiterte Funktionen:'
              : 'These cookies enable enhanced functionality:'}
          </p>
          <ul>
            <li>{language === 'de' ? 'Speicherung Ihrer Lernfortschritte' : 'Saving your learning progress'}</li>
            <li>{language === 'de' ? 'Personalisierte Empfehlungen' : 'Personalized recommendations'}</li>
            <li>{language === 'de' ? 'Chat-Verlauf zwischen Sitzungen' : 'Chat history between sessions'}</li>
          </ul>

          <h2>{language === 'de' ? '3. Cookies verwalten' : '3. Managing Cookies'}</h2>
          <p>
            {language === 'de' 
              ? 'Sie können Cookies in Ihren Browser-Einstellungen verwalten oder löschen. Bitte beachten Sie, dass das Deaktivieren bestimmter Cookies die Funktionalität der Website einschränken kann.'
              : 'You can manage or delete cookies in your browser settings. Please note that disabling certain cookies may limit the functionality of the website.'}
          </p>
          <p>
            {language === 'de' ? 'Anleitungen für gängige Browser:' : 'Instructions for common browsers:'}
          </p>
          <ul>
            <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700">Google Chrome</a></li>
            <li><a href="https://support.mozilla.org/de/kb/cookies-erlauben-und-ablehnen" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700">Mozilla Firefox</a></li>
            <li><a href="https://support.apple.com/de-de/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700">Safari</a></li>
            <li><a href="https://support.microsoft.com/de-de/microsoft-edge/cookies-in-microsoft-edge-löschen-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700">Microsoft Edge</a></li>
          </ul>

          <h2>{language === 'de' ? '4. Drittanbieter-Cookies' : '4. Third-Party Cookies'}</h2>
          <p>
            {language === 'de' 
              ? 'Wir nutzen Dienste von Drittanbietern, die eigene Cookies setzen können:'
              : 'We use third-party services that may set their own cookies:'}
          </p>
          <ul>
            <li><strong>Stripe:</strong> {language === 'de' ? 'Sichere Zahlungsabwicklung' : 'Secure payment processing'}</li>
            <li><strong>Google:</strong> {language === 'de' ? 'Analyse und Sprachdienste' : 'Analytics and language services'}</li>
          </ul>

          <h2>{language === 'de' ? '5. Änderungen' : '5. Changes'}</h2>
          <p>
            {language === 'de' 
              ? 'Wir können diese Cookie-Richtlinien jederzeit aktualisieren. Änderungen werden auf dieser Seite veröffentlicht.'
              : 'We may update this cookie policy at any time. Changes will be posted on this page.'}
          </p>

          <h2>{language === 'de' ? '6. Kontakt' : '6. Contact'}</h2>
          <p>
            {language === 'de' 
              ? 'Bei Fragen zu unseren Cookie-Richtlinien kontaktieren Sie uns bitte unter:'
              : 'If you have questions about our cookie policy, please contact us at:'}
          </p>
          <p>
            <strong>E-Mail:</strong> <a href="mailto:info@interlingua.app" className="text-emerald-600 hover:text-emerald-700">info@interlingua.app</a>
          </p>

          <div className="mt-8 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
            <p className="m-0">
              <strong>{language === 'de' ? 'Letzte Aktualisierung:' : 'Last Updated:'}</strong> {language === 'de' ? 'Januar 2026' : 'January 2026'}
            </p>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link to="/datenschutz" className="text-emerald-600 hover:text-emerald-700 underline" data-testid="link-datenschutz">
            {language === 'de' ? 'Datenschutzerklärung' : 'Privacy Policy'}
          </Link>
          <Link to="/agb" className="text-emerald-600 hover:text-emerald-700 underline" data-testid="link-agb">
            {language === 'de' ? 'AGB' : 'Terms & Conditions'}
          </Link>
          <Link to="/impressum" className="text-emerald-600 hover:text-emerald-700 underline" data-testid="link-impressum">
            {language === 'de' ? 'Impressum' : 'Imprint'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Cookies;
